import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { ChangeEvent, DragEvent, ReactElement } from 'react'
import type { MessageValues } from '@siteshowroom/i18n'
import { useLocale, useTranslate } from '../lib/i18n-provider'
import {
  MEDIA_ACCEPT_ATTRIBUTE,
  MEDIA_MAX_BYTES,
  MEDIA_MIN_WIDTH,
  inspectUpload,
  mediaErrorMessageKey,
  uploadMedia,
} from '../lib/media-client'

/**
 * Foto's aanleveren: kiezen of slepen, meerdere tegelijk.
 *
 * Drie keuzes die het gedrag bepalen:
 *
 * 1. **De bestanden gaan één voor één de deur uit.** Twintig gelijktijdige
 *    uploads van 20 MB verzadigen de uplink en lopen bovendien tegen de
 *    RateLimitGuard aan; achter elkaar duurt even lang en is voorspelbaar.
 * 2. **"Deze foto staat er al" is informatie, geen fout.** De API weigert een
 *    dubbele checksum binnen dezelfde residence met `conflict`. Voor de gebruiker
 *    is het resultaat precies wat hij wilde — de foto staat erbij — dus krijgt die
 *    melding een neutrale vorm en niet de rode van een mislukking.
 * 3. **De regel over wat mag, staat in de API.** De controle hieronder bespaart
 *    alleen een kansloze upload; de API oordeelt op de bytes (§17.2).
 */

/**
 * `checking`  het bestand wordt lokaal bekeken
 * `uploading` onderweg, met voortgang
 * `done`      toegevoegd
 * `skipped`   stond er al — informatie, geen fout
 * `rejected`  lokaal geweigerd (type, te groot, te klein)
 * `failed`    de API weigerde het of het lukte niet
 */
type UploadPhase = 'checking' | 'uploading' | 'done' | 'skipped' | 'rejected' | 'failed'

interface UploadEntry {
  readonly id: string
  /** Data uit het bestandssysteem, geen interfacetekst: bewust onvertaald. */
  readonly name: string
  readonly phase: UploadPhase
  /** Tussen 0 en 1; alleen betekenisvol tijdens `uploading`. */
  readonly progress: number
  readonly messageKey: string
  readonly messageValues: MessageValues | undefined
  /** Technische toelichting bij een fout; bewust onvertaald. */
  readonly detail: string
}

export interface MediaUploaderProps {
  readonly residenceId: string
  /** Aangeroepen na elke geslaagde upload, zodat het raster ververst. */
  readonly onUploaded: () => void
}

/**
 * Oplopende teller in plaats van `crypto.randomUUID`: die laatste bestaat alleen
 * in een secure context, en de ontwikkelserver draait op http via een IP-adres
 * (ADR-032). Een teller is hier ruim voldoende — de sleutel hoeft alleen binnen
 * deze lijst uniek te zijn.
 */
let entryCounter = 0
function nextEntryId(): string {
  entryCounter += 1
  return `upload-${String(entryCounter)}`
}

/** Voorstelling van de fase in de opmaak; bepaalt kleur én toon van de melding. */
function toneOf(phase: UploadPhase): 'busy' | 'ok' | 'info' | 'error' {
  switch (phase) {
    case 'checking':
    case 'uploading':
      return 'busy'
    case 'done':
      return 'ok'
    case 'skipped':
      return 'info'
    case 'rejected':
    case 'failed':
      return 'error'
  }
}

export function MediaUploader({ residenceId, onUploaded }: MediaUploaderProps): ReactElement {
  const t = useTranslate()
  const { locale } = useLocale()
  const headingId = useId()
  const [entries, setEntries] = useState<readonly UploadEntry[]>([])
  const [dragging, setDragging] = useState(false)

  // De wachtrij is een keten van beloftes: elk bestand begint pas als het vorige
  // klaar is. Een ref en geen state — de keten hoort geen render te veroorzaken.
  const queueRef = useRef<Promise<void>>(Promise.resolve())
  const abortRef = useRef<AbortController | null>(null)
  if (abortRef.current === null) {
    abortRef.current = new AbortController()
  }

  useEffect(() => {
    const controller = abortRef.current
    return () => {
      // Bij het verlaten van het scherm heeft een lopende upload geen ontvanger
      // meer; hem laten doorlopen levert alleen verkeer op dat niemand leest.
      controller?.abort()
    }
  }, [])

  const percentFormat = useMemo(
    () => new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }),
    [locale],
  )

  const patchEntry = useCallback((id: string, patch: Partial<UploadEntry>): void => {
    setEntries((current) =>
      current.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)),
    )
  }, [])

  const processFile = useCallback(
    async (id: string, file: File): Promise<void> => {
      const signal = abortRef.current?.signal
      if (signal?.aborted === true) {
        return
      }

      const rejection = await inspectUpload(file)
      if (rejection !== null) {
        patchEntry(id, {
          phase: 'rejected',
          messageKey: `dashboard.media.upload.reject.${rejection}`,
          messageValues:
            rejection === 'tooLarge'
              ? { limitMb: MEDIA_MAX_BYTES / (1024 * 1024) }
              : rejection === 'tooSmall'
                ? { minWidth: MEDIA_MIN_WIDTH }
                : undefined,
        })
        return
      }

      patchEntry(id, { phase: 'uploading', progress: 0 })
      const result = await uploadMedia(residenceId, file, {
        onProgress: (fraction) => {
          patchEntry(id, { progress: fraction })
        },
        signal,
      })

      if (result.ok) {
        patchEntry(id, { phase: 'done', progress: 1, messageKey: 'dashboard.media.upload.done' })
        onUploaded()
        return
      }

      // Zie de kop van dit bestand: een dubbele foto is geen mislukking. Het
      // raster ververst wel, want de bestaande foto is het antwoord op de vraag.
      if (result.error.code === 'conflict') {
        patchEntry(id, { phase: 'skipped', messageKey: 'dashboard.media.upload.duplicate' })
        onUploaded()
        return
      }

      if (result.error.kind === 'aborted') {
        patchEntry(id, {
          phase: 'failed',
          messageKey: 'dashboard.media.upload.cancelled',
          detail: '',
        })
        return
      }

      patchEntry(id, {
        phase: 'failed',
        messageKey: mediaErrorMessageKey(result.error),
        detail: result.error.message,
      })
    },
    [onUploaded, patchEntry, residenceId],
  )

  const enqueue = useCallback(
    (files: readonly File[]): void => {
      if (files.length === 0) {
        return
      }
      const queued = files.map((file) => ({ id: nextEntryId(), file }))

      setEntries((current) => [
        ...current,
        ...queued.map(({ id, file }): UploadEntry => ({
          id,
          name: file.name,
          phase: 'checking',
          progress: 0,
          messageKey: 'dashboard.media.upload.checking',
          messageValues: undefined,
          detail: '',
        })),
      ])

      for (const { id, file } of queued) {
        queueRef.current = queueRef.current.then(() => processFile(id, file))
      }
    },
    [processFile],
  )

  function handleInputChange(event: ChangeEvent<HTMLInputElement>): void {
    enqueue(Array.from(event.target.files ?? []))
    // Leegmaken, anders vuurt `change` niet wanneer iemand hetzelfde bestand
    // opnieuw kiest — bijvoorbeeld na een mislukte poging.
    event.target.value = ''
  }

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault()
    setDragging(false)
    enqueue(Array.from(event.dataTransfer.files))
  }

  function handleDragOver(event: DragEvent<HTMLDivElement>): void {
    // Zonder dit opent de browser het bestand in een nieuw tabblad.
    event.preventDefault()
    setDragging(true)
  }

  const finished = entries.some(
    (entry) => entry.phase !== 'checking' && entry.phase !== 'uploading',
  )

  return (
    <section className="media-uploader" aria-labelledby={headingId}>
      <h2 className="media-uploader__heading" id={headingId}>
        {t('dashboard.media.upload.title')}
      </h2>

      <div
        className={dragging ? 'dropzone dropzone--active' : 'dropzone'}
        onDragOver={handleDragOver}
        onDragLeave={() => {
          setDragging(false)
        }}
        onDrop={handleDrop}
      >
        <p className="dropzone__hint">{t('dashboard.media.upload.dropHint')}</p>
        {/* Het invoerveld staat ín het label: visueel verborgen, maar wel in de
            tabvolgorde, zodat kiezen ook zonder muis werkt. De focusring komt via
            `:focus-within` op het label terecht — een verborgen veld kan hem zelf
            niet tonen. */}
        <label className="button dropzone__choose">
          {t('dashboard.media.upload.choose')}
          <input
            accept={MEDIA_ACCEPT_ATTRIBUTE}
            className="visually-hidden"
            multiple
            onChange={handleInputChange}
            type="file"
          />
        </label>
        <p className="dropzone__rules">
          {t('dashboard.media.upload.rules', {
            minWidth: MEDIA_MIN_WIDTH,
            limitMb: MEDIA_MAX_BYTES / (1024 * 1024),
          })}
        </p>
      </div>

      {entries.length > 0 && (
        <>
          <ul className="upload-list" aria-live="polite">
            {entries.map((entry) => (
              <li className="upload-item" data-tone={toneOf(entry.phase)} key={entry.id}>
                <span className="upload-item__name">{entry.name}</span>
                <span className="upload-item__message">
                  {t(entry.messageKey, entry.messageValues)}
                </span>
                {entry.phase === 'uploading' && (
                  <span className="upload-item__progress">
                    <progress max={1} value={entry.progress} />
                    <span className="upload-item__percent">
                      {percentFormat.format(entry.progress)}
                    </span>
                  </span>
                )}
                {entry.detail !== '' && <code className="upload-item__detail">{entry.detail}</code>}
              </li>
            ))}
          </ul>
          {finished && (
            <button
              className="button"
              onClick={() => {
                // Alleen wat af is verdwijnt; wat nog loopt blijft zichtbaar.
                setEntries((current) =>
                  current.filter(
                    (entry) => entry.phase === 'checking' || entry.phase === 'uploading',
                  ),
                )
              }}
              type="button"
            >
              {t('dashboard.media.upload.clear')}
            </button>
          )}
        </>
      )}
    </section>
  )
}
